import { useMemo } from 'react';
import {
  MailboxImportBody,
  MailboxImportedRef,
  MailboxSearchResult,
  MailboxSearchRouteQuery,
  MemberExternalAccountCalendarManualImportInput,
  MemberExternalAccountCalendarManualImportResult,
  MemberExternalAccountImportPreferencesSafe,
  MemberExternalAccountImportPreferencesUpdateInput,
  MemberExternalAccountMessageManualImportInput,
  MemberExternalAccountMessageManualImportResult,
  MemberExternalAccountCalendarsResult,
} from '@vivport/backend/types';
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import {
  disconnectMemberExternalAccount,
  importMailboxMessagesForAccount,
  listMailboxImportedRefsForAccount,
  listMemberExternalAccounts,
  listCalendarsForAccount,
  manualImportMessagesForMemberExternalAccount,
  manualImportCalendarForAccount,
  searchMailboxForAccount,
  startGoogleMemberExternalConnect,
  startMicrosoftMemberExternalConnect,
  updateMemberExternalAccountImportPreferences,
} from '@/api/member-external-account-api';
import { contentKeys } from './useContentQueries';
import { useAsyncStore } from '@/store/asyncStore';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';

export const memberExternalAccountKeys = {
  all: ['memberExternalAccounts'] as const,
  lists: () => [...memberExternalAccountKeys.all, 'list'] as const,
  list: (memberId: number) => [...memberExternalAccountKeys.lists(), memberId] as const,
  calendars: () => [...memberExternalAccountKeys.all, 'calendars'] as const,
  calendar: (memberExternalAccountId: number) =>
    [...memberExternalAccountKeys.calendars(), memberExternalAccountId] as const,
  mailboxImportedRefs: () => [...memberExternalAccountKeys.all, 'mailbox-imported-refs'] as const,
  mailboxImportedRefsForAccount: (memberExternalAccountId: number) =>
    [...memberExternalAccountKeys.mailboxImportedRefs(), memberExternalAccountId] as const,
  mailboxImportedRefsQuery: (memberExternalAccountId: number, sortedProviderMessageIds: string[]) =>
    [...memberExternalAccountKeys.mailboxImportedRefsForAccount(memberExternalAccountId), sortedProviderMessageIds] as const,
  mailboxSearches: () => [...memberExternalAccountKeys.all, 'mailbox-search'] as const,
  mailboxSearch: (memberExternalAccountId: number, params: MailboxSearchRouteQuery) =>
    [...memberExternalAccountKeys.mailboxSearches(), memberExternalAccountId, params] as const,
};

export type MailboxImportedRefMap = Map<string, MailboxImportedRef>;

export const MAILBOX_SEARCH_PAGE_SIZE = 25;

export const useMailboxSearchInfinite = (
  memberExternalAccountId: number,
  params: MailboxSearchRouteQuery,
  enabled: boolean
) => {
  return useInfiniteQuery<
    MailboxSearchResult,
    Error,
    InfiniteData<MailboxSearchResult, string | undefined>,
    ReturnType<typeof memberExternalAccountKeys.mailboxSearch>,
    string | undefined
  >({
    queryKey: memberExternalAccountKeys.mailboxSearch(memberExternalAccountId, params),
    queryFn: ({ pageParam }) =>
      searchMailboxForAccount(
        memberExternalAccountId,
        pageParam === undefined ? params : { ...params, pageToken: pageParam }
      ),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextPageToken ?? undefined,
    enabled,
    staleTime: 30_000,
  });
};

export function invalidateMailboxImportedRefs(queryClient: QueryClient, memberExternalAccountId?: number): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey:
      memberExternalAccountId === undefined
        ? memberExternalAccountKeys.mailboxImportedRefs()
        : memberExternalAccountKeys.mailboxImportedRefsForAccount(memberExternalAccountId),
  });
}

export const useMailboxImportedRefs = (memberExternalAccountId: number, providerMessageIds: string[]) => {
  const sortedIds = useMemo(() => [...new Set(providerMessageIds)].sort(), [providerMessageIds]);
  return useQuery({
    queryKey: memberExternalAccountKeys.mailboxImportedRefsQuery(memberExternalAccountId, sortedIds),
    queryFn: () => listMailboxImportedRefsForAccount(memberExternalAccountId, { providerMessageIds: sortedIds }),
    select: (result): MailboxImportedRefMap => new Map(result.refs.map((ref) => [ref.providerMessageId, ref])),
    enabled: sortedIds.length > 0,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
};

export const useKeepMailboxMessages = (memberExternalAccountId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: MailboxImportBody) => importMailboxMessagesForAccount(memberExternalAccountId, body),
    onSuccess: async () => {
      await Promise.all([
        invalidateMailboxImportedRefs(queryClient, memberExternalAccountId),
        queryClient.invalidateQueries({ queryKey: contentKeys.lists() }),
      ]);
    },
  });
};

const invalidateMemberExternalAccountLists = async (
  queryClient: ReturnType<typeof useQueryClient>,
  memberId?: number
): Promise<void> => {
  if (typeof memberId === 'number' && memberId > 0) {
    await queryClient.invalidateQueries({ queryKey: memberExternalAccountKeys.list(memberId) });
    return;
  }

  await queryClient.invalidateQueries({ queryKey: memberExternalAccountKeys.lists() });
};

export const useMemberExternalAccounts = (memberId: number) => {
  return useQuery({
    queryKey: memberExternalAccountKeys.list(memberId),
    queryFn: listMemberExternalAccounts,
    enabled: memberId > 0,
  });
};

export const useCalendarsForMemberExternalAccounts = (memberExternalAccountIds: number[]) => {
  const uniqueIds = Array.from(
    new Set(memberExternalAccountIds.filter((value) => Number.isInteger(value) && value > 0))
  );
  const queryResults = useQueries({
    queries: uniqueIds.map((memberExternalAccountId) => ({
      queryKey: memberExternalAccountKeys.calendar(memberExternalAccountId),
      queryFn: () => listCalendarsForAccount(memberExternalAccountId),
      enabled: memberExternalAccountId > 0,
    })),
  });

  const isLoading = queryResults.some((query) => query.isLoading);
  const isError = queryResults.some((query) => query.isError);
  const data: MemberExternalAccountCalendarsResult[] = queryResults
    .map((query) => query.data)
    .filter((value): value is MemberExternalAccountCalendarsResult => value !== undefined);

  return {
    isLoading,
    isError,
    data,
  };
};

export const useStartGoogleMemberExternalConnect = () => {
  return useMutation({
    mutationFn: startGoogleMemberExternalConnect,
  });
};

export const useStartMicrosoftMemberExternalConnect = () => {
  return useMutation({
    mutationFn: startMicrosoftMemberExternalConnect,
  });
};

export const useDisconnectMemberExternalAccount = (memberId?: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: disconnectMemberExternalAccount,
    onSuccess: async (result) => {
      await Promise.all([
        invalidateMemberExternalAccountLists(queryClient, memberId),
        queryClient.invalidateQueries({ queryKey: memberExternalAccountKeys.calendar(result.id) }),
      ]);
    },
  });
};

export const useManualImportMessagesForMemberExternalAccount = (memberId?: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: number;
      input: MemberExternalAccountMessageManualImportInput;
    }) => manualImportMessagesForMemberExternalAccount(id, input),
    onMutate: () => {
      const taskId = useAsyncStore.getState().addTask({
        title: 'Importing messages…',
        description: 'Import in progress.',
        status: 'processing',
      });
      return { taskId };
    },
    onSuccess: async (
      result: MemberExternalAccountMessageManualImportResult,
      _vars,
      context
    ) => {
      if (context?.taskId) {
        useAsyncStore.getState().updateTask(context.taskId, {
          status: 'completed',
          description: `Imported ${result.importedCount}, updated ${result.updatedCount}.`,
        });
      }
      await Promise.all([
        invalidateMemberExternalAccountLists(queryClient, memberId),
        queryClient.invalidateQueries({ queryKey: contentKeys.lists() }),
      ]);
    },
    onError: (error: unknown, _vars, context) => {
      if (context?.taskId) {
        useAsyncStore.getState().updateTask(context.taskId, {
          status: 'failed',
          description: getAxiosErrorDisplayMessage(error, 'Message import failed.'),
        });
      }
    },
  });
};

export const useManualImportCalendarForAccount = (memberId?: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: number;
      input: MemberExternalAccountCalendarManualImportInput;
    }) => manualImportCalendarForAccount(id, input),
    onMutate: () => {
      const taskId = useAsyncStore.getState().addTask({
        title: 'Importing calendar…',
        description: 'Import in progress.',
        status: 'processing',
      });
      return { taskId };
    },
    onSuccess: async (
      result: MemberExternalAccountCalendarManualImportResult,
      _vars,
      context
    ) => {
      if (context?.taskId) {
        useAsyncStore.getState().updateTask(context.taskId, {
          status: 'completed',
          description: `Imported ${result.importedCount}, updated ${result.updatedCount}.`,
        });
      }
      await Promise.all([
        invalidateMemberExternalAccountLists(queryClient, memberId),
        queryClient.invalidateQueries({ queryKey: contentKeys.lists() }),
      ]);
    },
    onError: (error: unknown, _vars, context) => {
      if (context?.taskId) {
        useAsyncStore.getState().updateTask(context.taskId, {
          status: 'failed',
          description: getAxiosErrorDisplayMessage(error, 'Calendar import failed.'),
        });
      }
    },
  });
};

export const useUpdateMemberExternalAccountImportPreferences = (memberId?: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: number;
      input: MemberExternalAccountImportPreferencesUpdateInput;
    }) => updateMemberExternalAccountImportPreferences(id, input),
    onSuccess: async (_result: MemberExternalAccountImportPreferencesSafe) => {
      await invalidateMemberExternalAccountLists(queryClient, memberId);
    },
  });
};
